/**
 * MODULE: _shared/frontend-shell/src/hooks/billing/useSubscription.ts
 * PURPOSE: Fetch current subscription from auth service (no mock in production)
 * DEPENDENCIES: @tanstack/react-query, @core/auth, getApiBaseUrl
 * PUBLIC_API: useSubscription, Subscription, BILLING_SUBSCRIPTION_QUERY_KEY
 * SIDE_EFFECTS: GET /api/v1/auth/subscription/current when authenticated
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~150 / 150
 * RECENT: 2026-07-17 [2.2.1608] — Gate query on auth so login does not race a stale null into /subscription-setup.
 * See: AuthenticationPage.tsx, AuthGuard.tsx, SubscriptionPage.tsx
 */
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@core/auth';
import { getApiBaseUrl } from '@shared/lib/frontend/api/config';

const API_BASE = `${getApiBaseUrl()}/api/v1/auth`;

/** Shared query key prefix — invalidate after checkout / voucher redeem. */
export const BILLING_SUBSCRIPTION_QUERY_KEY = ['billing', 'subscription'] as const;

export interface Subscription {
  planId: string;
  planName: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  status: 'active' | 'trialing' | 'canceled' | 'past_due' | 'incomplete';
  renewsAt?: string;
  trialEndsAt?: string;
  source?: string | null;
  hasStripeCustomer?: boolean;
  cardLast4?: string;
  cardBrand?: string;
  cardExpMonth?: number;
  cardExpYear?: number;
}

function mapBackendSubscription(data: {
  plan_tier?: string | null;
  status?: string | null;
  current_period_end?: string | null;
  source?: string | null;
  has_stripe_customer?: boolean;
  card_brand?: string | null;
  card_last4?: string | null;
  card_exp_month?: number | null;
  card_exp_year?: number | null;
}): Subscription | null {
  if (!data.plan_tier && !data.status) return null;
  const tier = (data.plan_tier ?? 'brazn_pro') as string;
  const isPro = tier === 'brazn_pro' || tier === 'professional';
  return {
    planId: isPro ? 'brazn_pro' : tier,
    planName: isPro ? 'Brazn Pro' : tier,
    price: isPro ? 99 : 0,
    currency: 'usd',
    interval: 'month',
    status: (data.status as Subscription['status']) ?? 'incomplete',
    renewsAt: data.current_period_end ?? undefined,
    source: data.source,
    hasStripeCustomer: Boolean(data.has_stripe_customer),
    cardBrand: data.card_brand ?? undefined,
    cardLast4: data.card_last4 ?? undefined,
    cardExpMonth: data.card_exp_month ?? undefined,
    cardExpYear: data.card_exp_year ?? undefined,
  };
}

const MOCK_SUBSCRIPTION_KEY = 'brazn-mock-subscription';
const useMock = import.meta.env.DEV && import.meta.env.VITE_MOCK_SUBSCRIPTION === 'true';

function getMockSubscription(): Subscription {
  const stored = localStorage.getItem(MOCK_SUBSCRIPTION_KEY);
  if (stored) {
    try {
      return JSON.parse(stored) as Subscription;
    } catch {
      /* ignore */
    }
  }
  const defaultSub: Subscription = {
    planId: 'brazn_pro',
    planName: 'Brazn Pro',
    price: 99,
    currency: 'usd',
    interval: 'month',
    status: 'active',
    renewsAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
  };
  localStorage.setItem(MOCK_SUBSCRIPTION_KEY, JSON.stringify(defaultSub));
  return defaultSub;
}

async function fetchSubscription(): Promise<Subscription | null> {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token');
  if (!token) {
    return useMock ? getMockSubscription() : null;
  }
  try {
    const response = await fetch(`${API_BASE}/subscription/current`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (response.status === 404 || response.status === 401) {
      return useMock ? getMockSubscription() : null;
    }
    if (!response.ok) {
      throw new Error('Failed to fetch subscription');
    }
    const data = await response.json();
    const mapped = mapBackendSubscription(data);
    if (!mapped || mapped.status === 'incomplete') {
      return useMock ? getMockSubscription() : null;
    }
    return mapped;
  } catch (err) {
    if (useMock) {
      if (import.meta.env.DEV) {
        console.info('Subscription API unavailable, using mock:', err);
      }
      return getMockSubscription();
    }
    return null;
  }
}

export function useSubscription() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const authed = Boolean(isAuthenticated);
  const query = useQuery<Subscription | null, Error>({
    // Include auth in the key so login invalidates the pre-login null cache.
    queryKey: [...BILLING_SUBSCRIPTION_QUERY_KEY, authed],
    queryFn: fetchSubscription,
    enabled: !authLoading && authed,
    retry: 1,
  });

  // Paywalls must wait until an authenticated fetch settles — never treat a
  // pre-login null (or disabled query) as "no subscription".
  const isLoading = authLoading || (authed && (query.isPending || query.isFetching));

  return {
    ...query,
    isLoading,
  };
}
